import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import { apiRequest } from '../../utils';

/* My wallet: the tickets a person holds, grouped by event, with the loose ones
   (not yet bound to an event) last. A seat carries the same QR the check-in uses,
   so it doubles as a scannable pass. Backend: GET /api/my-tickets. */
const TicketCard = ({ ticket, t }) => {
  const [showQr, setShowQr] = useState(false);
  return (
    <Card className="mb-2">
      <div className="d-flex justify-content-between align-items-start" style={{ gap: '0.5rem' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>
            {ticket.emoji ? `${ticket.emoji} ` : ''}{ticket.type_name || t('ticket')}
          </div>
          {ticket.team_name && (
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>{ticket.team_name}</div>
          )}
          <div className="mt-1" style={{ fontSize: '0.85rem' }}>
            {ticket.code && <span className="text-muted me-2">{ticket.code}</span>}
            {ticket.payment_status === 'unpaid' && (
              <span className="badge bg-warning-subtle text-warning-emphasis">{t('unpaid')}</span>
            )}
            {ticket.status === 'used' && (
              <span className="badge bg-secondary-subtle text-secondary-emphasis">{t('used')}</span>
            )}
          </div>
        </div>
        {ticket.qr_token && (
          <button type="button" className="btn btn-sm btn-light flex-shrink-0"
                  onClick={() => setShowQr((v) => !v)}>
            <Icon name="qrcode" className="me-1" />{showQr ? t('hide') : t('showQr')}
          </button>
        )}
      </div>
      {ticket.qr_token && showQr && (
        <div className="text-center mt-3">
          <QRCodeSVG value={ticket.qr_token} size={180} level="M" includeMargin={false} />
          <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>{t('showQrHint')}</div>
        </div>
      )}
    </Card>
  );
};

const Wallet = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await apiRequest('GET', '/my-tickets'));
      } catch {
        setData({ groups: [], unassigned: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = data?.groups || [];
  const unassigned = data?.unassigned || [];
  const available = data?.available || [];
  const isEmpty = groups.length === 0 && unassigned.length === 0 && available.length === 0;

  return (
    <Base activeNav="user">
      <Title>
        <Icon name="wallet" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('wallet')}
      </Title>

      {loading && <Loading size="lg" />}

      {!loading && isEmpty && (
        <Card className="text-center">
          <Icon name="ticket" theme="muted" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-0">{t('walletEmpty')}</p>
        </Card>
      )}

      {!loading && available.length > 0 && (
        <Card className="mb-3">
          <div className="mb-2" style={{ fontWeight: 600 }}>{t('availableTickets')}</div>
          {available.map((a, i) => (
            <div key={i} className="d-flex justify-content-between align-items-center py-1">
              <span style={{ fontWeight: 600 }}>
                {a.emoji ? `${a.emoji} ` : ''}{a.type_name || t('ticket')}
                {/* A ticket that came back from a cancelled booking says where it came from;
                    one bought as part of a membership has no origin to name. */}
                {(a.credits || []).length > 0 && (
                  <span className="text-muted d-block" style={{ fontWeight: 400, fontSize: '0.85rem' }}>
                    {t('creditFromLab', { lab: a.credits[0].event_name })}
                  </span>
                )}
              </span>
              <span className="text-muted">×{a.count}</span>
            </div>
          ))}
          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>{t('registerHint')}</p>
        </Card>
      )}

      {!loading && groups.map((g) => (
        <div key={g.event_id} className="mb-3">
          <div className="mb-2" style={{ fontWeight: 600 }}>
            {g.event_name}
            {g.event_date && (
              <span className="text-muted ms-2" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
                {new Date(g.event_date).toLocaleDateString()}
              </span>
            )}
          </div>
          {g.tickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} t={t} />)}
        </div>
      ))}

      {!loading && unassigned.length > 0 && (
        <div className="mb-3">
          <div className="mb-2" style={{ fontWeight: 600 }}>{t('walletUnassigned')}</div>
          {unassigned.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} t={t} />)}
        </div>
      )}
    </Base>
  );
};

export default Wallet;
